import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';

const ProductCard = ({ product, showRemoveButton = false }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const isWishlisted = isInWishlist(product.id);

  const handleProduct = () => {
    navigate(`/products/${product.id}`);
  };

  // ✅ Add to cart + remove from wishlist
  const handleCartIcon = (e) => {
    e.stopPropagation();

    const variantKey = [product.id].join("|");

    const productData = {
      id: product.id,
      key: variantKey,
      name: product.name,
      image: product.image,
      category: product.category || "Uncategorized",
      color: product.colors || "N/A",
      fabric: product.fabrics || "N/A",
      price: product.price ? `${product.price}` : "N/A",
      offer_price: product.offer_price ? `${product.offer_price}` : "N/A",
      priceValue: product.priceValue,
      quantity: 1,
    };

    // ✅ 1. Add to cart
    addToCart(productData);

    // ✅ 2. Remove from wishlist only if it exists there
    if (isWishlisted) {
      removeFromWishlist(product.id);
      // Dispatch custom event so UI updates immediately
      window.dispatchEvent(new CustomEvent('wishlistUpdated'));
    }

    // ✅ 3. Navigate to cart
    navigate('/cart');
  };

  const handleWishlistToggle = (e) => {
    e.stopPropagation();
    const productData = {
      id: product.id,
      image: product.image,
      name: product.title ?? product.name,
      price: product.price,
    };
    if (isWishlisted) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(productData);
    }
    window.dispatchEvent(new CustomEvent('wishlistUpdated'));
  };

  return (
    <article className="grow font-medium max-md:mt-4">
      <div className="relative">
        <img
          onClick={handleProduct}
          src={product.image}
          alt={product.name}
          className="object-contain cursor-pointer w-full aspect-[0.66]"
        />
        <button
          className="absolute top-4 right-4 p-1 bg-white bg-opacity-80 rounded-full shadow-md hover:shadow-lg transition-all duration-200 hover:bg-opacity-100"
          aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
          onClick={handleWishlistToggle}
        >
          <Heart
            size={24}
            strokeWidth={1.5}
            className={`transition-colors duration-200 ${
              isWishlisted
                ? 'fill-red-500 text-red-500'
                : 'text-gray-600 hover:text-red-400'
            }`}
          />
        </button>
      </div>

      <div className="w-full">
        <div className="flex items-start pt-3 pb-3 w-full text-xs text-neutral-700">
          <div className="flex flex-col items-start min-w-[260px]">
            <div className="overflow-hidden pr-2 min-h-[30px]">
              <h3 className="font-medium text-neutral-700">{product.name}</h3>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between w-full">
          <div className="self-stretch my-auto">
            <div className="flex items-center gap-2 text-sm text-neutral-700">
              {/* Offer Price */}
              {product.offer_price !== "N/A" && (
                <span className="font-medium text-yellow-600">
                  ₹ {product.offer_price}/-
                </span>
              )}

              {/* Original Price */}
              {product.price !== "N/A" && (
                <span className="font-medium text-neutral-500 line-through">
                  ₹ {product.price}/-
                </span>
              )}  

              {/* Discount */}
              {product.price !== "N/A" &&
              product.offer_price !== "N/A" &&
              Number(product.price) > Number(product.offer_price) && (
                <span className="text-xs text-green-600">
                  ({Math.round(((Number(product.price) - Number(product.offer_price)) / Number(product.price)) * 100)}% off)
                </span>
              )}
            </div>

          </div>

          <button
            className="flex items-center justify-center p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            aria-label={`Add ${product.name} to cart`}
            type="button"
            onClick={handleCartIcon}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-neutral-700 hover:text-yellow-500"
              aria-hidden="true"
            >
              <path
                d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.5 5.1 16.5H17M17 13V16.5M9 19.5C9.8 19.5 10.5 20.2 10.5 21S9.8 22.5 9 22.5 7.5 21.8 7.5 21 8.2 19.5 9 19.5ZM20 19.5C20.8 19.5 21.5 20.2 21.5 21S20.8 22.5 20 22.5 18.5 21.8 18.5 21 19.2 19.5 20 19.5Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
